import React, { useCallback, useContext, useEffect, useState } from "react";
import { Redirect, Route, type RouteComponentProps, Switch } from "react-router-dom";

import { AuthConsumer, AuthContext } from "~/lib/auth/index";
import { notifyDesktopAppOfAuth } from "~/lib/desktopApi/userAuthStatus";
import { ProtectedRoute } from "~/lib/routing";
import { setReferringEntities } from "~/lib/segment";
import { ModalRoot } from "~/ui";

import {
	Alias,
	CaptivePortal,
	DesktopSplash,
	FindByEmail,
	FindBySubdomain,
	Login,
	LoginGuest,
	LoginJoin,
	Root,
	Signup,
	SignupConfirm,
	SignupJoin,
	ZoomRoot,
} from "./steps";

import "~/ui/legacy-text-styles.css";
import "~/ui/transitions.css";
import "~/ui/variables.css";

notifyDesktopAppOfAuth();

const Index: React.FunctionComponent<RouteComponentProps> = props => {
	const { url } = props.match;

	const params = new URLSearchParams(props.location.search);
	const next = params.get("next") || "/";

	// Check if the current URL includes "redirectBackToDesktopOnLastOauthStep=1"
	const redirectBackToDesktopOnLastOauthStep = params.get("redirectBackToDesktopOnLastOauthStep") === "1";

	setReferringEntities();

	// Auth pages don't necessarily make GraphQL requests, so trigger
	// this manually to hide the loader.
	useEffect(() => {
		document.dispatchEvent(new CustomEvent("network-quiesced"));
	}, []);

	const authContext = useContext(AuthContext);
	const [isInitialized, setIsInitialized] = useState(false);

	// this key is also defined in oauth_redirect_popup.html
	const SIGNUP_DATA_KEY = "signupData";
	useEffect(() => {
		// don't want to override an existing auth context
		if (!authContext.state) {
			try {
				const signupData = localStorage.getItem(SIGNUP_DATA_KEY);

				if (signupData) {
					authContext.setAuthContext(JSON.parse(signupData));
				}
			} catch (error) {
				console.error("Failed to access localStorage for signup data:", error);
			}
		}
		// this is outside the if because we don't want stale auth context hanging around local storage
		try {
			localStorage.removeItem(SIGNUP_DATA_KEY);
		} catch (error) {
			console.error("Failed to remove signup data from localStorage:", error);
		}
		setIsInitialized(true);
	}, [authContext]);

	const handleFail = useCallback(() => <Redirect to={url} />, [url]);

	// Don't render the routes until we've checked for sessionStorage data
	if (!isInitialized) {
		return null;
	}

	return (
		<div className="main-container page">
			<AuthConsumer>
				{value => {
					// If the user is logged in and configured, redirect them to the next page.
					//
					// The exception being if we are instructed to redirect back to the desktop app. In that
					// case the desktop app needs to authenticate the user, and if the user is logged in or not
					// in the browser is irrelevant. We want to show the login page content and initiate the
					// OAuth flow.
					if (value.user?.configured && !redirectBackToDesktopOnLastOauthStep) {
						return <Redirect to={next} />;
					} else {
						return (
							<ModalRoot>
								<Switch>
									<Redirect
										from={`${url}/logout/`}
										to={{ pathname: `${url}/login/`, state: { message: "You are now logged out. Have a great day!" } }}
									/>

									<ProtectedRoute
										fail={handleFail}
										authorized={!!value.member_accounts}
										path={`${url}/login/join/`}
										component={LoginJoin}
									/>
									<ProtectedRoute
										fail={handleFail}
										authorized={!!value.state}
										path={`${url}/signup/join/`}
										component={SignupJoin}
									/>
									<ProtectedRoute
										fail={handleFail}
										authorized={!!value.state}
										path={`${url}/signup/confirm/`}
										component={SignupConfirm}
									/>

									<Route path={`${url}/login/guest/`} component={LoginGuest} />
									<Route path={`${url}/login/`} component={Login} />
									<Route path={`${url}/zoom/`} component={ZoomRoot} />
									<Route path={`${url}/signup/`} component={Signup} />
									<Route path={`${url}/alias/`} component={Alias} />
									<Route path={`${url}/find/subdomain/`} component={FindBySubdomain} />
									<Route path={`${url}/find/email/`} component={FindByEmail} />
									<Route path={`${url}/captive/`} component={CaptivePortal} />
									<Route path={`${url}/desktop-splash/`} component={DesktopSplash} />
									<Route exact path={`${url}/`} component={Root} />
									<Redirect to={`${url}/`} />
								</Switch>
							</ModalRoot>
						);
					}
				}}
			</AuthConsumer>
		</div>
	);
};

export default Index;
